import React from 'react'
import { Alert, Box, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material'
import { ArrowBack, Gavel } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

const SectionCard: React.FC<{
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
}> = ({ title, icon, children }) => {
  return (
    <Card sx={{ border: '1px solid rgba(255,255,255,0.10)', backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            {icon && (
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2.5,
                  display: 'grid',
                  placeItems: 'center',
                  background:
                    'linear-gradient(135deg, rgba(0,230,118,0.22), rgba(0,217,255,0.18), rgba(255,109,0,0.14))',
                  border: '1px solid rgba(255,255,255,0.10)'
                }}
              >
                {icon}
              </Box>
            )}
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {title}
            </Typography>
          </Stack>
          <Box>{children}</Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

const BulletList: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 2.75,
        color: 'text.secondary',
        '& li': { mb: 0.5 },
        '& li:last-of-type': { mb: 0 }
      }}
    >
      {children}
    </Box>
  )
}

const RegrasPage: React.FC = () => {
  const navigate = useNavigate()

  return (
    <Box sx={{ py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        <Stack spacing={2.5}>
          <Stack spacing={1}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
              sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
            >
              Voltar
            </Button>

            <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
              Regras do servidor
            </Typography>
            <Typography sx={{ color: 'text.secondary', maxWidth: 980 }}>
              Estas regras existem para manter o servidor PvE justo, cooperativo e agradável para todos.
            </Typography>
          </Stack>

          <Alert severity="info" icon={<Gavel fontSize="inherit" />}>
            Ao jogar no servidor, você concorda em seguir as regras abaixo. A administração pode aplicar punições em caso
            de descumprimento.
          </Alert>

          <SectionCard title="🏗️ 1. CONSTRUÇÃO E BASES">
            <Stack spacing={1.75}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Proibido bloquear recursos essenciais</Typography>
                <Typography sx={{ color: 'text.secondary' }}>Não construir sobre:</Typography>
                <BulletList>
                  <li>Spawn de minério, carvão, enxofre ou quartzo</li>
                  <li>Dungeons</li>
                  <li>Torres de Boss</li>
                  <li>Pontos estratégicos de teleporte</li>
                </BulletList>
                <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                  Bases devem manter acesso público a recursos naturais importantes.
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Proibido “reservar território”</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Não é permitido espalhar fundações ou objetos apenas para marcar área.
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>Construir somente onde houver intenção real de uso.</Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Bloqueio de passagem</Typography>
                <Typography sx={{ color: 'text.secondary' }}>Não bloquear:</Typography>
                <BulletList>
                  <li>Pontes naturais</li>
                  <li>Gargalos de montanha</li>
                  <li>Entradas de dungeon</li>
                </BulletList>
                <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                  Sempre manter passagem para mounts e Pals grandes.
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>📏 Distância mínima entre bases</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Recomenda-se manter uma distância razoável entre bases de diferentes jogadores.
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>Respeite o espaço e a estética do mapa.</Typography>
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="🐾 2. INTERAÇÃO COM PALS">
            <Stack spacing={1.75}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Proibido puxar Pals agressivos para bases alheias</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Não atrair mobs fortes para destruir base de outros players.
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>Não usar mecânicas de IA para causar dano indireto.</Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Roubo de captura</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Se um player iniciou combate e está capturando um Pal, não interfira.
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>Respeitar o “primeiro engajamento”.</Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>
                  ❌ Matar Pals raros que outro player está tentando capturar
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>Especialmente Alpha e Pals de evento.</Typography>
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="🧱 3. CONVIVÊNCIA ENTRE PLAYERS">
            <Stack spacing={1.75}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Exploits e bugs</Typography>
                <Typography sx={{ color: 'text.secondary' }}>Proibido utilizar bugs para:</Typography>
                <BulletList>
                  <li>Duplicar itens</li>
                  <li>Farm infinito</li>
                  <li>Quebrar economia</li>
                </BulletList>
                <Typography sx={{ color: 'text.secondary', mt: 1 }}>Exploits devem ser reportados à administração.</Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Poluição visual intencional</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Bases feitas apenas para “bagunçar o mapa” poderão ser removidas.
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Construções ofensivas</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Estruturas com nomes ou formatos ofensivos não serão toleradas.
                </Typography>
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="🌾 4. ECONOMIA E RECURSOS">
            <Stack spacing={1.75}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Monopolização de recursos</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Não dominar todos os spawns de um recurso específico.
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Servidor PvE é cooperativo, não competitivo por escassez.
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Abuso de mercado (caso exista economia interna)</Typography>
                <Typography sx={{ color: 'text.secondary' }}>Proibido manipular preços com práticas abusivas.</Typography>
                <Typography sx={{ color: 'text.secondary' }}>Proibido usar múltiplas contas para burlar limites.</Typography>
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="🏕️ 5. BASE CAMP E GUILD">
            <Stack spacing={1.75}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>📌 Limite de bases</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Respeitar o limite de Base Camps definido pelo servidor.
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Bases abandonadas poderão ser removidas após X dias offline.
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Guild abusiva</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Não usar guild apenas para expandir limite de bases artificialmente.
                </Typography>
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="💬 6. CHAT E COMPORTAMENTO">
            <Stack spacing={1.75}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Toxicidade</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  Racismo, preconceito, spam e assédio resultam em punição.
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900 }}>❌ Divulgação externa sem autorização</Typography>
                <Typography sx={{ color: 'text.secondary' }}>Proibido divulgar outros servidores.</Typography>
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="⚙️ 7. ADMINISTRAÇÃO">
            <Stack spacing={1.75}>
              <Typography sx={{ color: 'text.secondary' }}>
                Admins podem remover estruturas que prejudiquem o servidor.
              </Typography>

              <Box>
                <Typography sx={{ color: 'text.secondary' }}>Reincidência pode resultar em:</Typography>
                <BulletList>
                  <li>Advertência</li>
                  <li>Remoção de base</li>
                  <li>Ban temporário</li>
                  <li>Ban permanente</li>
                </BulletList>
              </Box>
            </Stack>
          </SectionCard>

          <Alert severity="warning">
            <Typography sx={{ fontWeight: 900, mb: 0.5 }}>🎯 PRINCÍPIO DO SERVIDOR</Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Se sua ação prejudica a experiência de outro player, quebra progressão justa ou explora falhas do jogo, ela
              provavelmente é proibida.
            </Typography>
          </Alert>
        </Stack>
      </Container>
    </Box>
  )
}

export default RegrasPage
